import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormBuilder, Validators} from '@angular/forms';
import {NavigationFunctionData} from '@application/helper/navigation-helper/navigation-function-data.interface';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {Equipment} from '@domain/machine/equipment';
import {DrawingConfigurationWeaveQuality} from '@domain/production-schedule/drawing-configuration-weave-quality';
import {DrawingRecolorInformation} from '@domain/production-schedule/drawing-recolor-information';
import {Permission} from '@domain/profile/permission.enum';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {DownloadDrawingComponent} from '@presentation/components/download/download-drawing.component';
import {BaseDrawingConfigurationComponent} from '@presentation/components/drawing-configuration/base-drawing-configuration.component';
import {SelectFinishingComponent} from '@presentation/components/select-finishing/select-finishing.component';
import {SelectMachineQualityComponent} from '@presentation/components/select-machine-quality/select-machine-quality.component';
import {SelectMachineComponent} from '@presentation/pages/texfab/production-schedule/add/initial-preparations-new/select-machine/select-machine.component';
import {SelectColorSetComponent} from '@presentation/pages/textile-data/colored-yarn-set/add/select-color-set/select-color-set.component';
import {OverviewListFinishing} from '@presentation/pages/textile-data/finishing/overview/overview-list-finishing';
import {AssertionUtils, DialogBuilderFactoryService, DialogComponentData, TranslateService} from '@vdw/angular-component-library';
import {get, isEqual, isNil} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {DrawingConfigurationForEpDrawing} from '../../drawing-configuration-for-ep-drawing';
import {DrawingConfigurationForm} from '../drawing-configuration-form';
import {NavigationSaveAsEpData} from './navigation-save-as-ep-data.interface';

@Component({
  selector: 'app-save-as-ep',
  templateUrl: './save-as-ep.component.html',
  styleUrls: ['./save-as-ep.component.scss']
})
export class SaveAsEpComponent extends BaseDrawingConfigurationComponent implements OnInit {
  @Input() public drawingConfigurationForEpDrawing: DrawingConfigurationForEpDrawing = {};
  @Input() public drawingName: string;
  @Input() public form: DrawingConfigurationForm;
  @Output() public closeSaveAsEp: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeDrawingDetails: EventEmitter<void> = new EventEmitter<void>();
  @Output() public epSaved: EventEmitter<void> = new EventEmitter<void>();

  public readonly QUALITY_CHIP_VALUE_KEY = '%(technicalNameWithVersion)';
  public readonly QUALITY_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath;
  public readonly FINISHING_CHIP_VALUE_KEY = '%(nameWithVersion)';
  public readonly FINISHING_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editFinishing.absolutePath;
  public readonly COLOR_SET_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.colorSet.editColorSet.absolutePath;
  public readonly FINISHING_VIEW_PERMISSION = RouteUtils.paths.texStyle.finishingTemplate.editFinishingTemplate.requiredPermission;
  public readonly MACHINE_VIEW_PERMISSION = Permission.TEXEDIT_PREVIEW_BMP;
  public readonly MACHINE_BASE_ROUTER_LINK = RouteUtils.paths.texState.editMachine.absolutePath;

  protected WARNING_REASON = 'DESIGN_LIBRARY.DETAILS.SAVE_AS_EP.CONFIRM_MACHINE_QUALITY_SELECTION';

  public constructor(
    private readonly formBuilder: FormBuilder,
    @Inject(MACHINE_QUALITIES) qualities: MachineQualities,
    @Inject(DRAWINGS) drawings: Drawings,
    translate: TranslateService,
    dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly navigationHelperService: NavigationHelperService<NavigationSaveAsEpData>
  ) {
    super(qualities, drawings, translate, dialogBuilderFactoryService);
  }

  public ngOnInit(): void {
    if (AssertionUtils.isNullOrUndefined(this.form)) {
      this.form = this.formBuilder.group({
        name: this.formBuilder.control(null, Validators.required, AsyncUniqueValidator.createValidator(this.drawings, null)),
        finishing: this.formBuilder.control(null),
        quality: this.formBuilder.control(this.drawingConfigurationForEpDrawing.quality, Validators.required),
        colorSet: this.formBuilder.control(this.drawingConfigurationForEpDrawing.colorSet, Validators.required),
        machine: this.formBuilder.control(null)
      });
    }

    const getFunctionsBasedOnDialogComponent = new Map<any, NavigationFunctionData>([
      [SelectMachineQualityComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.getQuality(newItemId), reopenDialog: (): void => this.selectQuality()}],
      [SelectColorSetComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.getRecoloredImage(newItemId), reopenDialog: (): void => this.selectColorSet()}]
    ]);

    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData, {dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).loadNewItem({newItemId}),
      ({dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).reopenDialog()
    );
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationSaveAsEpData>({form: this.form, drawingConfigurationForEpDrawing: this.drawingConfigurationForEpDrawing});
  }

  public onCloseSaveAsEp(): void {
    this.closeSaveAsEp.emit();
  }

  public onCloseDrawingDetails(): void {
    this.closeDrawingDetails.emit();
  }

  public isFinishingSelected(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.form.value.finishing);
  }

  public selectFinishing(): void {
    this.getDialogBuiderForBasicDialogPanel()
      .openSelectGridDialog(SelectFinishingComponent, {machineQualityId: this.form.value.quality.id})
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedOverviewListFinishing: OverviewListFinishing) => {
        const finishingFormControl: AbstractControl = this.form.controls.finishing;
        const currentFinishing: OverviewListFinishing = finishingFormControl.value;
        if (!isNil(selectedOverviewListFinishing) && (!this.isFinishingSelected() || !isEqual(currentFinishing.id, selectedOverviewListFinishing.id))) {
          finishingFormControl.setValue(selectedOverviewListFinishing);
        }
      });
  }

  public selectMachine(): void {
    this.getDialogBuiderForBasicDialogPanel()
      .openSelectGridDialog(SelectMachineComponent, {
        machineQualityId: this.form.value.quality.id
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedMachine: Equipment) => {
        if (!AssertionUtils.isNullOrUndefined(selectedMachine)) {
          this.form.controls.machine.setValue(selectedMachine);
        }
      });
  }

  public getReadableLabelForFinishing(): string {
    return this.form.value.finishing.nameWithVersion;
  }

  public clearFinishing(): void {
    this.form.controls.finishing.setValue(null);
  }

  public canSaveAsEp(): boolean {
    return this.form.valid;
  }

  public applyDesignNameForEP(): void {
    this.form.controls.name.setValue(this.drawingName);
    this.form.controls.name.markAllAsTouched();
  }

  public canShowNameSuggestion(): boolean {
    return this.drawingName !== this.form.value.name;
  }

  public saveAsEp(): void {
    this.getDialogBuiderForBasicDialogPanel()
      .openDialog(DownloadDrawingComponent, {
        objectTranslationKey: 'DESIGN_LIBRARY.DETAILS.SAVE_AS_EP.SAVE_AS_EP',
        drawingImageId: this.getDrawingImageId(),
        designName: this.form.value.name,
        colorSetId: this.form.value.colorSet.id,
        drawingDimensionsInPx: this.drawingDimensionsInPx,
        finishingId: this.getFinishingId(),
        qualityId: this.getQualityId(),
        machineId: this.getMachineId()
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.epSaved.emit();
      });
  }

  protected handleMachineQualitySelection(chosenMachineQuality: DrawingConfigurationWeaveQuality): void {
    if (this.isColorSetSelected() || this.isFinishingSelected() || this.isMachineSelected()) {
      this.confirmMachineQualitySelection(chosenMachineQuality);
    } else {
      this.form.controls.quality.setValue(chosenMachineQuality);
    }
  }

  protected onConfirmMachineQualitySelection(machineQuality: DrawingConfigurationWeaveQuality): void {
    this.form.setValue({
      name: this.form.value.name,
      quality: machineQuality,
      colorSet: null,
      finishing: null,
      machine: null
    });
  }

  protected handleColorSetSelection(drawingRecolorInformation: DrawingRecolorInformation): void {
    this.form.controls.colorSet.setValue(drawingRecolorInformation.colorSet);
    this.drawingConfigurationForEpDrawing.image.data = drawingRecolorInformation.image;
    this.recoloringImage = false;
  }

  protected getDrawingImageId(): string {
    return this.drawingConfigurationForEpDrawing.image.id;
  }

  protected getDrawingThreadByThread(): boolean {
    return false;
  }

  protected getDrawingNumberOfSelectionColumns(): number {
    return 0;
  }

  private getFinishingId(): number {
    return get(this.form.value.finishing, 'id', null);
  }
}
